// Services das Configurações de pagamento — molde services/admin/processor-config.ts:
// queryOptions exportadas p/ teste, invalidação por predicate na chave base e
// toasts com a mensagem que o backend explicou.
//
// As REGRAS são imutáveis porque assinaturas e seguros apontam para a
// configuração — mudar a regra é criar outra. Só a descrição é editável (PATCH),
// porque não entra no hash de deduplicação do core.
import { api } from "@/api"
import {
  BASE_URL,
  ENDPOINTS,
  HTTP_STATUS_CODE,
} from "@/constants"
import { toast } from "@/hooks/use-toast"
import i18n from "@/i18n"
import { getApiErrorMessage } from "@/lib/api-error"
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@/libs"
import type {
  PaymentConfiguration,
  PaymentConfigurationCreatePayload,
  PaymentConfigurationCreateResult,
  PaymentConfigurationDescriptionPayload,
  PaymentConfigurationListFilters,
  PaymentConfigurationPageResponse,
} from "@/types/payment-configurations"

const toastPrefix = "pages.paymentConfigurations.toasts"

// Chave base de TODAS as queries da feature — usada também na invalidação.
export const PAYMENT_CONFIGURATIONS_QUERY_KEY =
  ENDPOINTS.PAYMENT_CONFIGURATIONS

/**
 * Mensagem do erro: a que o backend explicou ou a fallback.
 *
 * O interceptor do axios entrega o erro JÁ normalizado (`{ statusCode, message }`,
 * com `cause.errors` traduzido) — `getApiErrorMessage` cobre o corpo cru e o
 * `message` do normalizado cobre o resto.
 */
const errorMessageOf = (
  error: unknown,
  fallback: string
): string => {
  const fromBody = getApiErrorMessage(error, "")
  if (fromBody) return fromBody
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string" &&
    error.message
  ) {
    return error.message
  }
  return fallback
}

const urlOf = (template: string, id: string) =>
  `${BASE_URL}${template.replace("{{id}}", encodeURIComponent(id))}`

// Query string da listagem (nomes do contrato, camelCase).
export const buildListQueryString = (
  filters: PaymentConfigurationListFilters
): string => {
  const params = new URLSearchParams()
  if (filters.whiteLabelId)
    params.set("whiteLabelId", filters.whiteLabelId)
  if (filters.active !== undefined)
    params.set("active", String(filters.active))
  if (filters.page) params.set("page", String(filters.page))
  if (filters.pageSize)
    params.set("pageSize", String(filters.pageSize))
  const queryString = params.toString()
  return queryString ? `?${queryString}` : ""
}

// GET /payment-configurations
export const getPaymentConfigurations = async (
  filters: PaymentConfigurationListFilters = {}
): Promise<PaymentConfigurationPageResponse> => {
  const url = `${BASE_URL}${ENDPOINTS.PAYMENT_CONFIGURATIONS}${buildListQueryString(filters)}`
  const { data } =
    await api.get<PaymentConfigurationPageResponse>(url)
  return data
}

// GET /payment-configurations/:id
export const getPaymentConfiguration = async (
  id: string
): Promise<PaymentConfiguration> => {
  const { data } = await api.get<PaymentConfiguration>(
    urlOf(ENDPOINTS.PAYMENT_CONFIGURATION, id)
  )
  return data
}

// POST /payment-configurations — 201 criou, 200 reutilizou a idêntica.
export const createPaymentConfiguration = async (
  payload: PaymentConfigurationCreatePayload
): Promise<PaymentConfigurationCreateResult> => {
  const { data, status } =
    await api.post<PaymentConfiguration>(
      `${BASE_URL}${ENDPOINTS.PAYMENT_CONFIGURATIONS}`,
      payload
    )
  // 200 (não 201) = o core reutilizou uma com as mesmas regras.
  return {
    configuration: data,
    reused: status === HTTP_STATUS_CODE.SUCCESS,
  }
}

/**
 * PATCH /payment-configurations/:id — troca só a descrição (as regras são
 * imutáveis). Idempotente no core; `""` limpa a descrição.
 *
 * @param id id da configuração (`PCF-<uuid>`)
 * @param payload descrição nova
 * @returns a configuração atualizada
 */
export const updatePaymentConfigurationDescription = async (
  id: string,
  payload: PaymentConfigurationDescriptionPayload
): Promise<PaymentConfiguration> => {
  const { data } = await api.patch<PaymentConfiguration>(
    urlOf(ENDPOINTS.PAYMENT_CONFIGURATION, id),
    payload
  )
  return data
}

// POST /payment-configurations/:id/{activate|deactivate} — idempotentes.
export const togglePaymentConfiguration = async (
  id: string,
  action: "activate" | "deactivate"
): Promise<PaymentConfiguration> => {
  const template =
    action === "activate"
      ? ENDPOINTS.PAYMENT_CONFIGURATION_ACTIVATE
      : ENDPOINTS.PAYMENT_CONFIGURATION_DEACTIVATE
  const { data } = await api.post<PaymentConfiguration>(
    urlOf(template, id)
  )
  return data
}

/**
 * Opções da lista. O `keepPreviousData` global fica: trocar de página mantém a
 * lista anterior na tela (com `isPlaceholderData`) em vez de piscar o skeleton.
 */
export const paymentConfigurationsListQueryOptions = (
  filters: PaymentConfigurationListFilters = {}
) => ({
  queryKey: [
    PAYMENT_CONFIGURATIONS_QUERY_KEY,
    "list",
    filters,
  ] as const,
  queryFn: async () => getPaymentConfigurations(filters),
  // A tela mostra a falha no PaymentConfigurationFeedback; sem isto o
  // QueryCache global somaria um toast ao mesmo erro.
  meta: { skipErrorNotification: true },
})

/**
 * Opções do detalhe. `placeholderData: undefined` anula o keepPreviousData
 * global: ao trocar de id, mostrar o detalhe ANTERIOR seria mostrar a regra
 * errada sob o id novo.
 */
export const paymentConfigurationDetailQueryOptions = (
  id: string
) => ({
  queryKey: [
    PAYMENT_CONFIGURATIONS_QUERY_KEY,
    "detail",
    id,
  ] as const,
  queryFn: async () => getPaymentConfiguration(id),
  enabled: Boolean(id),
  placeholderData: undefined,
  // Idem lista: o erro aparece uma vez, no feedback da página.
  meta: { skipErrorNotification: true },
})

export const usePaymentConfigurations = (
  filters: PaymentConfigurationListFilters = {}
) =>
  useQuery(paymentConfigurationsListQueryOptions(filters))

export const usePaymentConfiguration = (id: string) =>
  useQuery(paymentConfigurationDetailQueryOptions(id))

// Toda query da feature (lista, detalhe) começa pela chave base.
const isFeatureQuery = (query: {
  queryKey: readonly unknown[]
}) => query.queryKey[0] === PAYMENT_CONFIGURATIONS_QUERY_KEY

export const useCreatePaymentConfigurationMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<
    PaymentConfigurationCreateResult,
    unknown,
    PaymentConfigurationCreatePayload
  >({
    mutationFn: createPaymentConfiguration,
    onSuccess: async ({ reused, configuration }) => {
      // 200 = já existia uma com as MESMAS regras (a descrição não entra no
      // hash): nada novo foi criado e a descrição é a antiga. O id diz qual foi
      // reutilizada — sem isso, quem lê procuraria uma "segunda" na lista.
      toast({
        title: reused
          ? i18n.t(`${toastPrefix}.reused`, {
              id: configuration.id,
            })
          : i18n.t(`${toastPrefix}.created`),
        variant: "success",
      })
      await queryClient.invalidateQueries({
        predicate: isFeatureQuery,
      })
    },
    onError: (error) => {
      toast({
        title: errorMessageOf(
          error,
          i18n.t(`${toastPrefix}.createError`)
        ),
        variant: "destructive",
      })
    },
  })
}

const useToggleMutation = (
  action: "activate" | "deactivate"
) => {
  const queryClient = useQueryClient()

  return useMutation<PaymentConfiguration, unknown, string>(
    {
      mutationFn: (id) =>
        togglePaymentConfiguration(id, action),
      onSuccess: (configuration) => {
        // O detalhe aberto reflete na hora; a lista consolida no settled.
        queryClient.setQueryData(
          paymentConfigurationDetailQueryOptions(
            configuration.id
          ).queryKey,
          configuration
        )
        toast({
          title:
            action === "activate"
              ? i18n.t(`${toastPrefix}.activated`)
              : i18n.t(`${toastPrefix}.deactivated`),
          variant: "success",
        })
      },
      onError: (error) => {
        toast({
          title: errorMessageOf(
            error,
            action === "activate"
              ? i18n.t(`${toastPrefix}.activateError`)
              : i18n.t(`${toastPrefix}.deactivateError`)
          ),
          variant: "destructive",
        })
      },
      onSettled: async () => {
        await queryClient.invalidateQueries({
          predicate: isFeatureQuery,
        })
      },
    }
  )
}

export const useActivatePaymentConfigurationMutation = () =>
  useToggleMutation("activate")

export const useDeactivatePaymentConfigurationMutation =
  () => useToggleMutation("deactivate")

/**
 * Mutation da edição de descrição: atualiza o detalhe no cache, mostra o toast
 * e invalida as queries da feature no settled.
 */
export const useUpdatePaymentConfigurationDescriptionMutation =
  () => {
    const queryClient = useQueryClient()

    return useMutation<
      PaymentConfiguration,
      unknown,
      {
        id: string
      } & PaymentConfigurationDescriptionPayload
    >({
      mutationFn: ({ id, description }) =>
        updatePaymentConfigurationDescription(id, {
          description,
        }),
      onSuccess: (configuration) => {
        // O detalhe aberto reflete na hora; a lista consolida no settled.
        queryClient.setQueryData(
          paymentConfigurationDetailQueryOptions(
            configuration.id
          ).queryKey,
          configuration
        )
        toast({
          title: i18n.t(
            `${toastPrefix}.descriptionUpdated`
          ),
          variant: "success",
        })
      },
      onError: (error) => {
        toast({
          title: errorMessageOf(
            error,
            i18n.t(`${toastPrefix}.descriptionUpdateError`)
          ),
          variant: "destructive",
        })
      },
      onSettled: async () => {
        await queryClient.invalidateQueries({
          predicate: isFeatureQuery,
        })
      },
    })
  }
